import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Layout from '../../components/Layout';
import BopExcelTable, { BopExcelColumn } from '../../components/BopExcelTable';
import ApiInspector from '../../components/ApiInspector';
import ComboFiltro from '../../components/ComboFiltro';
import { apiFetch } from '../../lib/api';
import { useFiltroSocieta } from '../../hooks/useFiltroSocieta';
import { useFiltriApplicati } from '../../hooks/useFiltriApplicati';
import BopLoaderOverlay from '../../components/BopLoaderOverlay';
import { formatNumber } from '../../utils/formatters';
import {
  BustaGo, ETICHETTE_ORIGINE, MisureCommessa, PeriodoGo, RispostaCommesse, StatoGo, VoceCommessa,
  applicaPeriodo, etichettaDataRiferimento, euro, mesiParziali,
} from '../../lib/gestionaleOpen';
import { API_DOCS } from './AnalisiCommesseGoPage.api';
import ModaleMovimentiCommessa, { VoceAperta } from './ModaleMovimentiCommessa';
import FiltroPeriodoGo, { periodoIniziale } from './FiltroPeriodoGo';
import { BottoneCercaGo, InvitoCercaGo } from './CercaGo';

/**
 * Analisi commesse: il bilancio di ogni commessa GO — ricavi, costi e margine
 * di TUTTI i movimenti di commessa (tabella cmm), non solo delle fatture.
 *
 * Dal 23/09/2026 la base sono i movimenti di commessa (vista
 * movimenti-commessa della Cloud Function): oltre alle fatture entrano DDT e
 * bolle, la prima nota (personale, compensi amministratori, ratei commesse,
 * rimanenze, rettifiche) e gli scarichi di magazzino. Ogni movimento compare
 * una volta sola: GO mette il movimento o sul DDT o sulla fattura che lo
 * chiude, mai su entrambi (verificato su tutti i database). La vecchia pagina
 * «solo fatture» dava margini piu' alti del vero dove pesa il costo interno.
 *
 * Il periodo si conta di default sulla data del movimento (la registrazione),
 * come il bilancio di commessa di GO: cosi' i totali coincidono con GO
 * (verificato: costi arc_solu 2026 2.361.735,96 come GO; per competenza erano
 * 138,36 € in meno, tre movimenti di competenza 2025 registrati nel 2026).
 * Si puo' scegliere la competenza o la data documento.
 *
 * Una societa' per volta, come le altre pagine GO: i codici commessa sono
 * locali a ciascun database.
 *
 * Il filtro commessa in testata porta la tabella sulle sottocommesse di quella
 * commessa; il clic su una riga apre il dettaglio (ModaleMovimentiCommessa).
 */

/** Margine % con una cifra decimale; senza ricavi non ha senso. */
function percentuale(v: number | null | undefined): string {
  return v === null || v === undefined ? '—' : `${formatNumber(v)}%`;
}

function Margine({ valore }: { valore: number }) {
  return <span className={valore < 0 ? 'bop-text-danger' : undefined}>{euro(valore)}</span>;
}

type Origine = MisureCommessa & { origine: string };

export default function AnalisiCommesseGoPage() {
  const { societaId: societaSel, setSocietaId, listaSocieta, isUnica } = useFiltroSocieta();

  // Filtri: i controlli cambiano la bozza (…Sel), le letture usano i valori
  // applicati con «Cerca»
  const [periodoSel, setPeriodo] = useState<PeriodoGo>(() => periodoIniziale('registrazione'));

  // Tutte le commesse del periodo: tabella senza filtro ed elenco del filtro
  const [generale, setGenerale] = useState<RispostaCommesse | null>(null);
  const [stato, setStato] = useState<StatoGo>('OK');
  const [avviso, setAvviso] = useState('');
  const [errore, setErrore] = useState('');
  const [caricando, setCaricando] = useState(false);

  // Filtro commessa. Vale solo per la societa' in cui e' stato scelto: i
  // codici GO sono per database, e cosi' cambiare societa' lo azzera senza
  // una chiamata in piu' con il codice vecchio.
  const [scelta, setScelta] = useState<{ societaId: string; codice: string }>({ societaId: '', codice: '' });
  const commessaSel = scelta.societaId === societaSel ? scelta.codice : '';

  const { applicati, versione, applica, modificati, cercato } = useFiltriApplicati(
    { societaId: societaSel, periodo: periodoSel, commessa: commessaSel }, societaSel,
  );
  const { societaId, periodo, commessa } = applicati;

  // Sottocommesse della commessa filtrata
  const [filtrata, setFiltrata] = useState<RispostaCommesse | null>(null);
  const [caricandoFiltrata, setCaricandoFiltrata] = useState(false);
  const [erroreFiltrata, setErroreFiltrata] = useState('');

  const [aperta, setAperta] = useState<VoceAperta | null>(null);

  const parametri = useCallback((codiceCommessa?: string) => {
    const p = applicaPeriodo(new URLSearchParams({ societaId }), periodo);
    if (codiceCommessa) p.set('commessa', codiceCommessa);
    return p;
  }, [societaId, periodo]);

  useEffect(() => {
    if (!cercato || !societaId) { setGenerale(null); setStato('OK'); return undefined; }
    let annullato = false;
    (async () => {
      setCaricando(true);
      setErrore('');
      setAvviso('');
      setAperta(null);
      try {
        // @api GET /api/bop-go/dati-commerciali/commesse?societaId=1&anno=2025&data_su=competenza -> {"success":true,"stato":"OK","data":{"raggruppa":"commessa","commessa":null,"fonte":"movimenti","data_riferimento":"data_competenza","totali":{"ricavi":3659591.14,"costi":3361482.15,"margine":298108.99,"margine_pct":8.1,"movimenti":5161},"origini":[{"origine":"contabilita","ricavi":0,"costi":1257630.03,"margine":-1257630.03,"movimenti":230}],"fatture_non_collegate":{"ricavi":6491.86,"costi":142473.94},"data":[{"codice":"ESSELUNGA2025","etichetta":"ESSELUNGA 2025","ricavi":1647199.14,"costi":1072339.03,"margine":574860.11,"margine_pct":34.9,"movimenti":215}]}}
        const res = await apiFetch<BustaGo<RispostaCommesse>>(`/api/bop-go/dati-commerciali/commesse?${parametri().toString()}`);
        if (annullato) return;
        setStato(res.stato);
        setGenerale(res.data);
        if (res.stato !== 'OK' && res.error) setAvviso(res.error);
      } catch (err) {
        if (annullato) return;
        setErrore(err instanceof Error ? err.message : 'Errore nel recupero delle commesse da GO');
        setGenerale(null);
      } finally {
        if (!annullato) setCaricando(false);
      }
    })();
    return () => { annullato = true; };
  }, [cercato, societaId, parametri, versione]);

  useEffect(() => {
    setFiltrata(null);
    setErroreFiltrata('');
    setAperta(null);
    if (!cercato || !societaId || !commessa) return undefined;
    let annullato = false;
    (async () => {
      setCaricandoFiltrata(true);
      try {
        // @api GET /api/bop-go/dati-commerciali/commesse?societaId=1&anno=2025&commessa=SMARTILIO2025 -> {"success":true,"stato":"OK","data":{"raggruppa":"sottocommessa","commessa":"SMARTILIO2025","totali":{"ricavi":405413.23,"costi":64624.88,"margine":340788.35,"margine_pct":84.1,"movimenti":517},"origini":[],"fatture_non_collegate":null,"data":[{"codice":"1","etichetta":"SMARTILIO 2025","ricavi":405413.23,"costi":64624.88,"margine":340788.35,"margine_pct":84.1,"movimenti":517}]}}
        const res = await apiFetch<BustaGo<RispostaCommesse>>(`/api/bop-go/dati-commerciali/commesse?${parametri(commessa).toString()}`);
        if (annullato) return;
        setFiltrata(res.data);
        if (res.stato !== 'OK' && res.error) setErroreFiltrata(res.error);
      } catch (err) {
        if (annullato) return;
        setErroreFiltrata(err instanceof Error ? err.message : 'Errore nel recupero delle sottocommesse da GO');
      } finally {
        if (!annullato) setCaricandoFiltrata(false);
      }
    })();
    return () => { annullato = true; };
  }, [cercato, societaId, commessa, parametri, versione]);

  // Elenco del filtro: le commesse con movimenti nel periodo. Se quella scelta
  // non c'e' (cambio di periodo) resta in elenco, altrimenti il filtro attivo
  // sparirebbe dalla combo.
  const elencoCommesse = useMemo(() => {
    const elenco = (generale?.data ?? [])
      .filter((v) => v.codice)
      .map((v) => ({ id: String(v.codice), nome: v.etichetta ? `${v.etichetta} (${v.codice})` : String(v.codice) }))
      .sort((x, y) => x.nome.localeCompare(y.nome, 'it'));
    if (commessa && !elenco.some((c) => c.id === commessa)) elenco.unshift({ id: commessa, nome: commessa });
    return elenco;
  }, [generale, commessa]);

  const voceCommessa = useMemo(() => {
    const trovata = generale?.data.find((v) => v.codice === commessa);
    return { codice: commessa || null, etichetta: trovata?.etichetta ?? null };
  }, [generale, commessa]);

  const apriRiga = useCallback((v: VoceCommessa) => {
    if (commessa) {
      setAperta({ commessa: voceCommessa, sottocommessa: { codice: v.codice, etichetta: v.etichetta } });
    } else {
      setAperta({ commessa: { codice: v.codice, etichetta: v.etichetta } });
    }
  }, [commessa, voceCommessa]);

  const colonne = useCallback((etichettaGruppo: string, senza: string): BopExcelColumn<VoceCommessa>[] => [
    {
      key: 'etichetta',
      header: etichettaGruppo,
      accessor: (v) => v.etichetta ?? v.codice ?? '',
      render: (v) => (
        <span className="bop-analisi-go__gruppo">
          <span className="bop-analisi-go__gruppo-nome">
            {v.codice ? (v.etichetta ?? v.codice) : senza}
          </span>
          {v.codice && v.etichetta && <span className="bop-analisi-go__gruppo-codice">{v.codice}</span>}
        </span>
      ),
      filter: { type: 'text' },
    },
    {
      key: 'ricavi',
      header: 'Ricavi',
      accessor: (v) => v.ricavi,
      render: (v) => euro(v.ricavi),
      cellClassName: 'bop-text-right',
      headerClassName: 'bop-text-right',
      filter: { type: 'number' },
      width: '140px',
    },
    {
      key: 'costi',
      header: 'Costi',
      accessor: (v) => v.costi,
      render: (v) => euro(v.costi),
      cellClassName: 'bop-text-right',
      headerClassName: 'bop-text-right',
      filter: { type: 'number' },
      width: '140px',
    },
    {
      key: 'margine',
      header: 'Margine',
      accessor: (v) => v.margine,
      render: (v) => <Margine valore={v.margine} />,
      cellClassName: 'bop-text-right',
      headerClassName: 'bop-text-right',
      filter: { type: 'number' },
      width: '140px',
    },
    {
      key: 'margine_pct',
      header: 'Margine %',
      accessor: (v) => v.margine_pct ?? '',
      render: (v) => percentuale(v.margine_pct),
      cellClassName: 'bop-text-right',
      headerClassName: 'bop-text-right',
      width: '110px',
    },
    {
      key: 'movimenti',
      header: 'Movimenti',
      accessor: (v) => v.movimenti,
      cellClassName: 'bop-text-right',
      headerClassName: 'bop-text-right',
      width: '110px',
      defaultHidden: true,
    },
  ], []);

  const colonneCommesse = useMemo(() => colonne('Commessa', '(senza commessa)'), [colonne]);
  const colonneSotto = useMemo(() => colonne('Sottocommessa', '(senza sottocommessa)'), [colonne]);

  const colonneOrigini = useMemo<BopExcelColumn<Origine>[]>(() => [
    { key: 'origine', header: 'Origine dei movimenti', accessor: (o) => ETICHETTE_ORIGINE[o.origine] ?? o.origine },
    { key: 'ricavi', header: 'Ricavi', accessor: (o) => o.ricavi, render: (o) => euro(o.ricavi), cellClassName: 'bop-text-right', headerClassName: 'bop-text-right', width: '140px' },
    { key: 'costi', header: 'Costi', accessor: (o) => o.costi, render: (o) => euro(o.costi), cellClassName: 'bop-text-right', headerClassName: 'bop-text-right', width: '140px' },
    { key: 'margine', header: 'Margine', accessor: (o) => o.margine, render: (o) => <Margine valore={o.margine} />, cellClassName: 'bop-text-right', headerClassName: 'bop-text-right', width: '140px' },
    { key: 'movimenti', header: 'Movimenti', accessor: (o) => o.movimenti, cellClassName: 'bop-text-right', headerClassName: 'bop-text-right', width: '110px' },
  ], []);

  const risposta = commessa ? filtrata : generale;
  const caricandoTabella = commessa ? caricandoFiltrata : caricando;
  const t = risposta?.totali;
  const nonCollegate = generale?.fatture_non_collegate;
  const integrati = risposta?.integrati;
  const conIntegrati = !!integrati && (integrati.ricavi !== 0 || integrati.costi !== 0);

  return (
    <Layout title="Analisi commesse">
      <div className="bop-page-header">
        <h1 className="bop-page-title">Analisi commesse</h1>
        {commessa && stato === 'OK' && (
          <button className="bop-btn bop-btn-secondary" onClick={() => setAperta({ commessa: voceCommessa })}>
            Dettaglio della commessa
          </button>
        )}
      </div>

      <p className="bop-text-muted">
        Il bilancio di ogni commessa di Gestionale Open: ricavi, costi e margine di tutti i
        movimenti di commessa — fatture, DDT, prima nota (personale, ratei, rettifiche) e
        scarichi di magazzino. Ogni movimento conta una volta sola.
      </p>

      <div className="bop-filter-bar">
        {!isUnica && (
          <select className="bop-select" value={societaSel} onChange={(e) => setSocietaId(e.target.value)}>
            <option value="">Seleziona società</option>
            {listaSocieta.map((s) => <option key={s.id} value={s.id}>{s.nome}</option>)}
          </select>
        )}
        <FiltroPeriodoGo
          periodo={periodoSel}
          onChange={setPeriodo}
          dateAmmesse={['registrazione', 'competenza', 'documento']}
          etichette={{
            registrazione: 'Per data movimento (come GO)',
            competenza: 'Per data competenza',
            documento: 'Per data documento',
          }}
          conTuttiGliAnni
        />
        {cercato && societaId && stato === 'OK' && (
          <div className="bop-analisi-go__filtro-nominativo">
            <ComboFiltro
              label="Commessa"
              value={commessaSel || null}
              onChange={(id) => setScelta({ societaId: societaSel, codice: id ?? '' })}
              items={elencoCommesse}
            />
          </div>
        )}
        <BottoneCercaGo
          cercato={cercato}
          modificati={modificati}
          disabled={!societaSel || caricando || caricandoFiltrata}
          onClick={applica}
        />
      </div>

      <BopLoaderOverlay visibile={caricando || caricandoFiltrata} testo="Lettura delle commesse da Gestionale Open…" />

      {cercato && (mesiParziali(periodo) || periodo.intervallo) && (
        <p className="bop-filtro-periodo-go__nota">
          Il periodo taglia l'anno: ricavi e costi sono quelli registrati in questi mesi, non il
          margine della commessa. Se una commessa ha i costi in un mese e i ricavi fatturati dopo,
          qui il margine appare più basso di quello reale.
        </p>
      )}

      {!societaId && (
        <div className="bop-empty-state">
          Seleziona una società. I codici commessa sono locali a ciascun database GO:
          sommarli fra società darebbe un numero privo di significato.
        </div>
      )}

      {societaId && !cercato && <InvitoCercaGo />}

      {errore && <div className="bop-alert bop-alert--danger">{errore}</div>}
      {commessa && erroreFiltrata && <div className="bop-alert bop-alert--danger">{erroreFiltrata}</div>}

      {societaId && stato === 'NON_MAPPATA' && (
        <div className="bop-empty-state">
          Questa società non ha un database di Gestionale Open associato. Si imposta in
          {' '}<strong>Impostazioni Generali → Società del gruppo</strong>, campo «Database Gestionale Open».
        </div>
      )}

      {societaId && stato === 'NON_DISPONIBILE' && (
        <div className="bop-empty-state">
          <div className="bop-empty-state__title">Dati non ancora disponibili per questa società</div>
          <div className="bop-empty-state__text">{avviso}</div>
        </div>
      )}

      {societaId && stato === 'OK' && risposta && t && (
        <>
          <div className="bop-analisi-go__kpi">
            <div className="bop-analisi-go__stat">
              <div className="bop-analisi-go__stat-label">{commessa ? 'Ricavi della commessa' : 'Ricavi delle commesse'}</div>
              <div className="bop-analisi-go__stat-value">{euro(t.ricavi)}</div>
              <div className="bop-analisi-go__stat-sub">
                {t.movimenti} movimenti nel periodo
                {conIntegrati && integrati && integrati.ricavi !== 0 && ` · di cui ${euro(integrati.ricavi)} integrati da BOP`}
              </div>
            </div>
            <div className="bop-analisi-go__stat">
              <div className="bop-analisi-go__stat-label">{commessa ? 'Costi della commessa' : 'Costi delle commesse'}</div>
              <div className="bop-analisi-go__stat-value">{euro(t.costi)}</div>
              <div className="bop-analisi-go__stat-sub">personale e prima nota compresi</div>
            </div>
            <div className="bop-analisi-go__stat">
              <div className="bop-analisi-go__stat-label">Margine</div>
              <div className="bop-analisi-go__stat-value"><Margine valore={t.margine} /></div>
              <div className="bop-analisi-go__stat-sub">{percentuale(t.margine_pct)} dei ricavi</div>
            </div>
            {!commessa && nonCollegate && (nonCollegate.ricavi !== 0 || nonCollegate.costi !== 0) && (
              <div className="bop-analisi-go__stat bop-analisi-go__stat--attenzione">
                <div className="bop-analisi-go__stat-label">Fatture non collegate a commessa</div>
                <div className="bop-analisi-go__stat-value">{euro(nonCollegate.costi)}</div>
                <div className="bop-analisi-go__stat-sub">
                  di costi · {euro(nonCollegate.ricavi)} di ricavi, fuori dal bilancio delle commesse{' '}
                  <button
                    className="bop-analisi-go__link"
                    onClick={() => setAperta({ commessa: { codice: null, etichetta: null } })}
                  >
                    vedi fatture
                  </button>
                </div>
              </div>
            )}
          </div>

          {conIntegrati && integrati && (
            <div className="bop-alert bop-alert--warning">
              Comprende <strong>{euro(integrati.ricavi)}</strong> di ricavi
              {integrati.costi !== 0 && <> e <strong>{euro(integrati.costi)}</strong> di costi</>} da fatture con
              la commessa sulla riga ma <strong>senza movimento di commessa in Gestionale Open</strong>: sono le
              fatture create con le API di GO, per cui GO non crea il movimento. BOP le conta nella commessa
              indicata sulla fattura; nel bilancio di commessa di GO non compaiono. Nell'elenco per origine sono
              la voce «Fatture senza movimento in GO (integrate da BOP)».
            </div>
          )}

          {risposta.origini.length > 0 && (
            <div className="bop-analisi-go__origini">
              <BopExcelTable<Origine>
                data={risposta.origini}
                columns={colonneOrigini}
                rowKey={(o) => o.origine}
                initialSort={{ key: 'costi', dir: 'desc' }}
                storageKey="analisi-commesse-go-origini"
                emptyState={<div className="bop-empty-state">Nessun movimento</div>}
              />
            </div>
          )}

          <p className="bop-analisi-go__nota">
            Periodo contato sulla <strong>{etichettaDataRiferimento(risposta.data_riferimento)}</strong>.
            {commessa
              ? ' La tabella mostra le sottocommesse: clic su una riga per il dettaglio per origine, conto, movimenti e fatture.'
              : ' Clic su una riga per il dettaglio per origine, conto, movimenti e fatture; per le sottocommesse scegli la commessa nel filtro.'}
          </p>

          <BopExcelTable<VoceCommessa>
            data={risposta.data}
            columns={commessa ? colonneSotto : colonneCommesse}
            rowKey={(v) => v.codice ?? 'senza-codice'}
            loading={caricandoTabella}
            onRowClick={apriRiga}
            initialSort={{ key: 'ricavi', dir: 'desc' }}
            storageKey={commessa ? 'analisi-commesse-go-sottocommesse' : 'analisi-commesse-go'}
            emptyState={<div className="bop-empty-state">Nessun movimento di commessa nel periodo</div>}
          />
        </>
      )}

      {caricandoTabella && !risposta && (
        <div className="bop-empty-state">{commessa ? 'Lettura delle sottocommesse…' : 'Lettura delle commesse…'}</div>
      )}

      {aperta && (
        <ModaleMovimentiCommessa
          societaId={societaId}
          periodo={periodo}
          voce={aperta}
          onClose={() => setAperta(null)}
        />
      )}

      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}
